import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where, doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../firebase';
import './PopupAd.css';

function PopupAd({ categoryId = null, subcategoryId = null }) {
  const [ad, setAd] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  const fetchPopupAd = useCallback(async () => {
    try {
      const today = new Date();

      let q = query(
        collection(db, 'popupAds'),
        where('status', 'in', ['live', 'draft'])
      );

      const querySnapshot = await getDocs(q);
      let adsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter ads by category, subcategory, budget, and dates
      adsData = adsData.filter(ad => {
        // Check if ad has run out of budget
        const spend = ad.spend || 0;
        const budget = ad.budget || 0;
        if (spend >= budget) return false;

        // Check if ad is expired
        if (ad.runTill) {
          const runTill = ad.runTill.toDate ? ad.runTill.toDate() : new Date(ad.runTill);
          if (runTill < today) return false;
        }

        // Filter by category if provided
        if (categoryId) {
          if (ad.category !== categoryId && ad.categoryId !== categoryId) {
            return false;
          }
        }

        // Filter by subcategory if provided
        if (subcategoryId) {
          const adSubcategories = ad.subcategoryIds || ad.subCategoryIds || [];
          const adSubcategory = ad.subcategory || ad.subcategoryId || ad.subCategoryId;
          if (adSubcategory !== subcategoryId && !adSubcategories.includes(subcategoryId)) {
            return false;
          }
        }

        return true;
      });

      // Select a random ad if multiple are available
      if (adsData.length > 0) {
        const randomAd = adsData[Math.floor(Math.random() * adsData.length)];
        setAd(randomAd);
        
        // Show popup after a delay
        setTimeout(() => {
          setShowPopup(true);
          trackImpression(randomAd.id);
          sessionStorage.setItem('popupAdShown', 'true');
        }, 3000); // Show after 3 seconds
      }
    } catch (error) {
      console.error('Error fetching popup ad:', error);
    }
  }, [categoryId, subcategoryId]);

  useEffect(() => {
    // Check if we've already shown a popup in this session
    const popupShown = sessionStorage.getItem('popupAdShown');
    if (!popupShown) {
      fetchPopupAd();
    }
  }, [fetchPopupAd]);

  const trackImpression = async (adId) => {
    try {
      const adRef = doc(db, 'popupAds', adId);
      await updateDoc(adRef, {
        impressions: increment(1)
      });
    } catch (error) {
      console.error('Error tracking impression:', error);
    }
  };

  const trackClick = async (adId) => {
    try {
      const adRef = doc(db, 'popupAds', adId);
      await updateDoc(adRef, {
        clicks: increment(1),
        spend: increment(0.15) // CPC: $0.15 per click for popups
      });
    } catch (error) {
      console.error('Error tracking click:', error);
    }
  };

  const handleAdClick = () => {
    if (ad) {
      trackClick(ad.id);
      if (ad.url) {
        window.open(ad.url, '_blank', 'noopener,noreferrer');
      } else if (ad.businessId) {
        window.location.href = `/business/${ad.businessId}`;
      }
      closePopup();
    }
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  if (!showPopup || !ad) {
    return null;
  }

  return (
    <div className="popup-ad-overlay" onClick={closePopup}>
      <div className="popup-ad-container" onClick={(e) => e.stopPropagation()}>
        <button className="popup-close" onClick={closePopup}>×</button>
        <div 
          className="popup-ad-content"
          onClick={handleAdClick}
          style={{ cursor: ad.url || ad.businessId ? 'pointer' : 'default' }}
        >
          {ad.imageUrl && (
            <div className="popup-ad-image">
              <img src={ad.imageUrl} alt={ad.title || 'Advertisement'} />
            </div>
          )}
          <div className="popup-ad-info">
            {ad.title && <h3>{ad.title}</h3>}
            {ad.description && <p>{ad.description}</p>}
            {(ad.url || ad.businessId) && (
              <button className="popup-ad-btn">Learn More →</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PopupAd;
